import { useCallback, useContext } from 'react';
import { Edit2 as Edit, Trash } from 'react-feather';
import MenuItem from '@mui/material/MenuItem';
import { cx } from '../utils/style';
import { ThemeGroupContext, ThemeGroupSelectContext } from '../contexts';
import type { Theme } from '../types';
import styles from './Select.module.css';

type Props = {
  editable?: boolean;
  item?: Theme;
  readonly?: boolean;
};

/**
 * Individual theme option in the dropdown menu
 * Displays theme name, color swatches, and edit/delete buttons (if editable)
 */
export const SelectOption = (props: Props) => {
  const ctx = useContext(ThemeGroupContext);
  const select = useContext(ThemeGroupSelectContext);

  if (!ctx) {
    throw new Error('SelectOption must be used within ThemeGroupContext');
  }

  if (!select) {
    throw new Error('SelectOption must be used within ThemeGroupSelectContext');
  }

  // Handle case where no item is provided (e.g., loading state)
  if (!props.item) {
    return null;
  }

  const handleClick = useCallback(() => {
    if (!props.readonly) {
      ctx!.setTheme(props.item!.id);
      select!.close();
    }
  }, [
    props.item,
    props.readonly,
    ctx,
    select,
  ]);

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    ctx!.deletePalette(props.item!.id);
  }, [
    ctx,
    props.item,
  ]);

  const handleEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    ctx!.editPalette(props.item!.id);
    select!.close();
  }, [
    ctx,
    props.item,
    select,
  ]);

  const optionClasses = cx(styles.item, {
    [styles.editable]: props.editable,
    [styles.option]: !props.readonly,
    [styles.preset]: !props.editable && !props.readonly,
  });

  return (
    <MenuItem
      classes={{ root: cx(styles.input, { [styles.readonly]: props.readonly }) }}
      disableRipple>
      <div
        className={optionClasses}
        onClick={handleClick}>
        <div className={styles.info}>
          <div className={styles.name}>
            {props.item.name}
          </div>

          <div className={styles.details}>
            <div className={styles.palette}>
              {props.item.colors.slice(0, 20).map((x, i) =>
                <div
                  className={styles.color}
                  key={`${i}-${x}`}
                  style={{ backgroundColor: x }} />)}
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          {(props.editable && !props.readonly) &&
            <>
              <button
                className={styles.delete}
                onClick={handleDelete}>
                <Trash className={styles.icon} />
              </button>
              <button
                className={styles.edit}
                onClick={handleEdit}>
                <Edit className={styles.icon} />
              </button>
            </>}
        </div>
      </div>
    </MenuItem>
  );
};

SelectOption.displayName = 'ChartTheming.Select.Option';
